import React, { useState, useEffect } from "react";

const WorkFlow = () => {
  const [activeStep, setActiveStep] = useState(1);

  // Auto-highlight steps with 2 second gap
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev % 3) + 1);
    }, 2000);
    return () => clearInterval(timer);
  }, []);

  const steps = [
    {
      number: "01",
      title: "Start a free fundraiser",
      description: "Create your campaign in minutes. Add photos, tell your story, and set your goal. Our smart form guides you step by step.",
      gradient: "from-teal-600 to-emerald-600",
      lightGradient: "from-teal-50 to-emerald-50",
      bgPattern: "bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-teal-100/30 via-transparent to-transparent"
    },
    {
      number: "02",
      title: "Share your story",
      description: "Share your campaign with friends, family, and social networks. Built-in tools help you reach more people, faster.",
      gradient: "from-blue-600 to-indigo-600",
      lightGradient: "from-blue-50 to-indigo-50",
      bgPattern: "bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-100/30 via-transparent to-transparent"
    },
    {
      number: "03",
      title: "Receive help & give thanks",
      description: "Watch your community come together to support you. Every donation brings hope, and every supporter becomes part of your story.",
      gradient: "from-amber-600 to-orange-600",
      lightGradient: "from-amber-50 to-orange-50",
      bgPattern: "bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-amber-100/30 via-transparent to-transparent"
    },
  ];

  return (
    <section className="relative bg-white py-20 md:py-28 lg:py-32 overflow-hidden">
      {/* Dynamic background that changes with active step */}
      <div className="absolute inset-0 transition-all duration-1000">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50"></div>
        
        {/* Active step background effects */}
        {activeStep === 1 && (
          <>
            <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-teal-100/40 via-teal-50/20 to-transparent blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-emerald-100/30 via-transparent to-transparent blur-3xl"></div>
          </>
        )}
        
        {activeStep === 2 && (
          <>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-100/40 via-indigo-50/20 to-transparent blur-3xl"></div>
            <div className="absolute top-20 right-20 w-[400px] h-[400px] bg-blue-100/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-20 left-20 w-[400px] h-[400px] bg-indigo-100/20 rounded-full blur-3xl"></div>
          </>
        )}
        
        {activeStep === 3 && (
          <>
            <div className="absolute bottom-0 right-0 w-[800px] h-[800px] bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-amber-100/40 via-orange-50/20 to-transparent blur-3xl"></div>
            <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-orange-100/30 via-transparent to-transparent blur-3xl"></div>
            {/* Added warm community glow */}
            <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-amber-200/20 rounded-full blur-3xl"></div>
          </>
        )}
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #0d9488 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header - with active step indicator */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-white/80 backdrop-blur-sm text-gray-600 text-sm font-medium rounded-full border border-gray-200 shadow-sm mb-5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-600"></span>
            </span>
            <span>Simple · Transparent · Free</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 tracking-tight mb-5">
            Fundraising in{' '}
            <span className="relative">
              <span className={`relative z-10 transition-colors duration-700 ${
                activeStep === 1 ? 'text-teal-600' :
                activeStep === 2 ? 'text-blue-600' :
                'text-amber-600'
              }`}>
                three steps
              </span>
              <span className={`absolute -bottom-2 left-0 w-full h-0.5 transition-colors duration-700 ${
                activeStep === 1 ? 'bg-teal-200' :
                activeStep === 2 ? 'bg-blue-200' :
                'bg-amber-200'
              }`}></span>
            </span>
          </h2>
          
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            No hidden fees. No complex forms. Just a better way to raise money for what matters.
          </p>
          
          {/* Step indicator dots */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {[1, 2, 3].map((step) => (
              <button
                key={step}
                onClick={() => setActiveStep(step)}
                className={`transition-all duration-500 ${
                  activeStep === step
                    ? 'w-8 h-2 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full'
                    : 'w-2 h-2 bg-gray-300 hover:bg-gray-400 rounded-full'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Main Content - Two column layout */}
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-20 items-center">
          
          {/* LEFT - Premium Timeline Steps with auto-highlight */}
          <div className="flex-1 space-y-8 lg:space-y-10">
            {steps.map((step, index) => (
              <div
                key={index}
                onClick={() => setActiveStep(index + 1)}
                className={`group relative flex items-start gap-6 cursor-pointer transition-all duration-700 ${
                  activeStep === index + 1 
                    ? 'scale-[1.02] lg:scale-105' 
                    : 'opacity-60 hover:opacity-80'
                }`}
              >
                {/* Active state indicator - animated line */}
                {activeStep === index + 1 && (
                  <>
                    <div className="absolute -left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-teal-500 to-emerald-500 rounded-full animate-pulse"></div>
                    <div className="absolute -left-6 top-1/2 -translate-y-1/2 w-2 h-2 bg-teal-500 rounded-full animate-ping"></div>
                  </>
                )}
                
                {/* Number - with glow effect on active */}
                <div className="relative flex-shrink-0">
                  {activeStep === index + 1 && (
                    <div className={`absolute -inset-2 bg-gradient-to-r ${step.gradient} rounded-2xl blur-xl opacity-30 animate-pulse`}></div>
                  )}
                  <div className={`relative w-14 h-14 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center transition-all duration-700 ${
                    activeStep === index + 1
                      ? `bg-gradient-to-br ${step.gradient} text-white shadow-2xl scale-110`
                      : 'bg-white border-2 border-gray-200 text-gray-400 group-hover:border-gray-300'
                  }`}>
                    <span className="text-xl lg:text-2xl font-medium">
                      {step.number}
                    </span>
                  </div>
                  
                  {/* Connection line - animated */}
                  {index < steps.length - 1 && (
                    <div className="absolute top-16 left-1/2 -translate-x-1/2 w-px h-8 lg:h-10 overflow-hidden">
                      <div className={`w-full h-full bg-gradient-to-b transition-all duration-1000 ${
                        activeStep > index + 1 
                          ? 'from-teal-500 to-emerald-500 translate-y-0' 
                          : 'from-gray-200 to-transparent -translate-y-full'
                      }`}></div>
                    </div>
                  )}
                </div>

                {/* Content - with updated Step 3 messaging */}
                <div className="flex-1 pt-2">
                  <h3 className={`text-lg lg:text-xl font-semibold mb-2 transition-all duration-700 ${
                    activeStep === index + 1
                      ? `text-transparent bg-clip-text bg-gradient-to-r ${step.gradient} scale-105`
                      : 'text-gray-600 group-hover:text-gray-900'
                  }`}>
                    {step.title}
                  </h3>
                  <p className={`text-sm lg:text-base leading-relaxed transition-all duration-700 ${
                    activeStep === index + 1
                      ? 'text-gray-700'
                      : 'text-gray-400 group-hover:text-gray-500'
                  }`}>
                    {step.description}
                  </p>
                  
                  {/* Active step badge - customized for each step */}
                  {activeStep === index + 1 && (
                    <div className={`inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-xs font-medium ${
                      index === 0 ? 'bg-teal-50 text-teal-700' :
                      index === 1 ? 'bg-blue-50 text-blue-700' :
                      'bg-amber-50 text-amber-700'
                    }`}>
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                          index === 0 ? 'bg-teal-400' :
                          index === 1 ? 'bg-blue-400' :
                          'bg-amber-400'
                        }"></span>
                        <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                          index === 0 ? 'bg-teal-600' :
                          index === 1 ? 'bg-blue-600' :
                          'bg-amber-600'
                        }`}></span>
                      </span>
                      {index === 0 && 'Start your journey'}
                      {index === 1 && 'Share with love'}
                      {index === 2 && 'Feel the support'}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* Time indicator - animated */}
            <div className="flex items-center gap-3 pt-4 bg-white/50 backdrop-blur-sm p-4 rounded-2xl border border-gray-100">
              <div className="relative">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-white flex items-center justify-center relative">
                    <span className="text-xs font-medium text-gray-600">2m</span>
                    {activeStep === 1 && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-teal-500 rounded-full animate-ping"></span>
                    )}
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-white flex items-center justify-center">
                    <span className="text-xs font-medium text-gray-600">5m</span>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-white flex items-center justify-center">
                    <span className="text-xs font-medium text-gray-600">10m</span>
                  </div>
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-500">Average setup time</span>
                <span className="text-sm font-medium text-gray-900 ml-1">2 minutes</span>
              </div>
            </div>
          </div>

          {/* RIGHT - Premium Phone Mockup with dynamic glow */}
          <div className="flex-1 flex justify-center lg:justify-end">
            <div className="relative">
              {/* Dynamic glow based on active step */}
              <div className={`absolute -inset-4 rounded-[56px] transition-all duration-1000 blur-2xl ${
                activeStep === 1 ? 'bg-teal-400/20' :
                activeStep === 2 ? 'bg-blue-400/20' :
                'bg-amber-400/20'
              }`}></div>
              
              {/* Device mockup */}
              <div className="relative w-[280px] md:w-[320px]">
                {/* Device frame */}
                <div className="relative bg-white rounded-[48px] shadow-2xl border-[6px] border-gray-100 overflow-hidden">
                  
                  {/* Status bar - Dynamic color */}
                  <div className="absolute top-0 left-0 right-0 h-10 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-between px-6">
                    <span className="text-xs font-medium text-gray-600">9:41</span>
                    <div className="flex items-center gap-1">
                      <div className={`w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center transition-colors duration-700 ${
                        activeStep === 1 ? 'bg-teal-100' :
                        activeStep === 2 ? 'bg-blue-100' :
                        'bg-amber-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full transition-colors duration-700 ${
                          activeStep === 1 ? 'bg-teal-500' :
                          activeStep === 2 ? 'bg-blue-500' :
                          'bg-amber-500'
                        } animate-pulse`}></div>
                      </div>
                      <span className="text-xs font-medium text-gray-600">DonatePe</span>
                    </div>
                  </div>
                  
                  {/* Video container */}
                  <div className="pt-10 bg-gray-50">
                    <video
                      src="/assets/signup.mp4"
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-[500px] lg:h-[550px] object-cover"
                    />
                  </div>
                  
                  {/* Home indicator */}
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gray-200 rounded-full"></div>
                </div>
                
                {/* Floating stats card - Updated for Step 3 */}
                <div className={`absolute -bottom-5 -right-5 bg-white rounded-xl shadow-lg border p-4 min-w-[160px] transition-all duration-700 ${
                  activeStep === 1 ? 'border-teal-100' :
                  activeStep === 2 ? 'border-blue-100' :
                  'border-amber-100'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500">
                      {activeStep === 1 && 'Campaigns started'}
                      {activeStep === 2 && 'Social shares'}
                      {activeStep === 3 && 'Lives impacted'}
                    </span>
                    <span className={`text-xs font-medium transition-colors duration-700 ${
                      activeStep === 1 ? 'text-teal-600' :
                      activeStep === 2 ? 'text-blue-600' :
                      'text-amber-600'
                    }`}>
                      {activeStep === 1 && '+24%'}
                      {activeStep === 2 && '+156%'}
                      {activeStep === 3 && '2,847'}
                    </span>
                  </div>
                  <div className="flex items-end justify-between">
                    <span className="text-xl font-semibold text-gray-900">
                      {activeStep === 1 && '50k+'}
                      {activeStep === 2 && '125k+'}
                      {activeStep === 3 && '85k+'}
                    </span>
                    <span className="text-xs text-gray-400">
                      {activeStep === 1 && 'this month'}
                      {activeStep === 2 && 'shares'}
                      {activeStep === 3 && 'lives'}
                    </span>
                  </div>
                  <div className="mt-3 h-1 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`w-3/4 h-full bg-gradient-to-r transition-colors duration-700 ${
                      activeStep === 1 ? 'from-teal-500 to-emerald-500' :
                      activeStep === 2 ? 'from-blue-500 to-indigo-500' :
                      'from-amber-500 to-orange-500'
                    } rounded-full`}></div>
                  </div>
                </div>
                
                {/* Trust badge - Dynamic message for Step 3 */}
                <div className={`absolute -top-4 -right-4 bg-white rounded-full px-4 py-2 shadow-md border transition-colors duration-700 flex items-center gap-2 ${
                  activeStep === 1 ? 'border-teal-100' :
                  activeStep === 2 ? 'border-blue-100' :
                  'border-amber-100'
                }`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors duration-700 ${
                    activeStep === 1 ? 'bg-teal-50' :
                    activeStep === 2 ? 'bg-blue-50' :
                    'bg-amber-50'
                  }`}>
                    <svg className={`w-3 h-3 transition-colors duration-700 ${
                      activeStep === 1 ? 'text-teal-600' :
                      activeStep === 2 ? 'text-blue-600' :
                      'text-amber-600'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {activeStep === 1 && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      )}
                      {activeStep === 2 && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      )}
                      {activeStep === 3 && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      )}
                    </svg>
                  </div>
                  <span className="text-xs font-medium text-gray-700">
                    {activeStep === 1 && 'Verified platform'}
                    {activeStep === 2 && 'Share with confidence'}
                    {activeStep === 3 && 'Supported by community'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Trust Bar - Dynamic */}
        <div className="mt-20 lg:mt-24 text-center">
          <div className="inline-flex items-center gap-8 bg-white/80 backdrop-blur-sm px-8 py-4 rounded-full border shadow-sm transition-colors duration-700 ${
            activeStep === 1 ? 'border-teal-100' :
            activeStep === 2 ? 'border-blue-100' :
            'border-amber-100'
          }">
            <div className="flex items-center gap-2">
              <svg className={`w-5 h-5 transition-colors duration-700 ${
                activeStep === 1 ? 'text-teal-600' :
                activeStep === 2 ? 'text-blue-600' :
                'text-amber-600'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-5m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-sm text-gray-600">PCI DSS compliant</span>
            </div>
            <div className="w-px h-4 bg-gray-200"></div>
            <div className="flex items-center gap-2">
              <svg className={`w-5 h-5 transition-colors duration-700 ${
                activeStep === 1 ? 'text-teal-600' :
                activeStep === 2 ? 'text-blue-600' :
                'text-amber-600'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-gray-600">No platform fees</span>
            </div>
            <div className="w-px h-4 bg-gray-200"></div>
            <div className="flex items-center gap-2">
              <svg className={`w-5 h-5 transition-colors duration-700 ${
                activeStep === 1 ? 'text-teal-600' :
                activeStep === 2 ? 'text-blue-600' :
                'text-amber-600'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <span className="text-sm text-gray-600">Instant access</span>
            </div>
          </div>
          
          <p className="text-xs text-gray-400 mt-6 flex items-center justify-center gap-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-600"></span>
            </span>
            Trusted by 50,000+ fundraisers and 500,000+ donors
          </p>
        </div>
      </div>
    </section>
  );
};

export default WorkFlow;